import * as React from 'react';

import { IEnhancedTableCheckboxFilter, IEnhancedTableHeadCell, IEnhancedTableTextFilter } from 'models';
import { getCheckboxFilteredData, getTextFilteredData, initCheckboxFilters, initTextFilters } from 'utils';

type FilterState = {
  data: any[];
  defaultCheckboxFilters: IEnhancedTableCheckboxFilter;
  checkboxFilters: IEnhancedTableCheckboxFilter;
  defaultTextFilters: IEnhancedTableTextFilter;
  textFilters: IEnhancedTableTextFilter;
};

type FilterAction =
  | { type: 'initialize'; data: any[]; headColumns: IEnhancedTableHeadCell[] }
  | { type: 'clear' }
  | { type: 'updateCheckbox'; filterName: string; key: string; value: boolean }
  | { type: 'selectAllCheckbox'; filterName: string }
  | { type: 'unselectAllCheckbox'; filterName: string }
  | { type: 'updateText'; filterName: string; value: string };

type UseEnhancedTableFiltersProps = {
  data: any[];
  headColumns: IEnhancedTableHeadCell[];
  onFilteredDataChange?: (filteredData: any[]) => void;
};

const createFilterState = (data: any[], headColumns: IEnhancedTableHeadCell[]): FilterState => {
  const defaultCheckboxFilters = initCheckboxFilters(data, headColumns);
  const defaultTextFilters = initTextFilters(headColumns);

  return {
    data,
    defaultCheckboxFilters,
    checkboxFilters: defaultCheckboxFilters,
    defaultTextFilters,
    textFilters: defaultTextFilters,
  };
};

const filterReducer = (state: FilterState, action: FilterAction): FilterState => {
  switch (action.type) {
    case 'initialize':
      return createFilterState(action.data, action.headColumns);
    case 'clear':
      return {
        ...state,
        checkboxFilters: state.defaultCheckboxFilters,
        textFilters: state.defaultTextFilters,
      };
    case 'updateCheckbox':
      return {
        ...state,
        checkboxFilters: {
          ...state.checkboxFilters,
          [action.filterName]: {
            ...state.checkboxFilters[action.filterName],
            [action.key]: action.value,
          },
        },
      };
    case 'selectAllCheckbox':
      return {
        ...state,
        checkboxFilters: {
          ...state.checkboxFilters,
          [action.filterName]: { ...state.defaultCheckboxFilters[action.filterName] },
        },
      };
    case 'unselectAllCheckbox':
      return {
        ...state,
        checkboxFilters: {
          ...state.checkboxFilters,
          [action.filterName]: Object.fromEntries(
            Object.keys(state.checkboxFilters[action.filterName]).map((key) => [key, false]),
          ),
        },
      };
    case 'updateText':
      return {
        ...state,
        textFilters: {
          ...state.textFilters,
          [action.filterName]: action.value,
        },
      };
    default:
      return state;
  }
};

export const useEnhancedTableFilters = ({ data, headColumns, onFilteredDataChange }: UseEnhancedTableFiltersProps) => {
  const [state, dispatch] = React.useReducer(
    filterReducer,
    { data, headColumns },
    ({ data: initialData, headColumns: initialHeadColumns }) => createFilterState(initialData, initialHeadColumns),
  );
  const onFilteredDataChangeRef = React.useRef(onFilteredDataChange);

  onFilteredDataChangeRef.current = onFilteredDataChange;

  React.useEffect(() => {
    dispatch({ type: 'initialize', data, headColumns });
  }, [data, headColumns]);

  const filteredData = React.useMemo(() => {
    const textFilteredData = getTextFilteredData(state.data, state.textFilters);

    return getCheckboxFilteredData(textFilteredData, state.checkboxFilters);
  }, [state.data, state.textFilters, state.checkboxFilters]);

  React.useEffect(() => {
    onFilteredDataChangeRef.current?.(filteredData);
  }, [filteredData]);

  const clearFilters = React.useCallback(() => dispatch({ type: 'clear' }), []);
  const updateCheckboxFilters = React.useCallback(
    (filterName: string, key: string, value: boolean) => dispatch({ type: 'updateCheckbox', filterName, key, value }),
    [],
  );
  const selectAllCheckboxFilters = React.useCallback(
    (filterName: string) => dispatch({ type: 'selectAllCheckbox', filterName }),
    [],
  );
  const unselectAllCheckboxFilters = React.useCallback(
    (filterName: string) => dispatch({ type: 'unselectAllCheckbox', filterName }),
    [],
  );
  const updateTextFilters = React.useCallback(
    (filterName: string, value: string) => dispatch({ type: 'updateText', filterName, value }),
    [],
  );

  return {
    defaultCheckboxFilters: state.defaultCheckboxFilters,
    checkboxFilters: state.checkboxFilters,
    textFilters: state.textFilters,
    filteredData,
    clearFilters,
    updateCheckboxFilters,
    selectAllCheckboxFilters,
    unselectAllCheckboxFilters,
    updateTextFilters,
  };
};
